import { z } from 'zod';
import { HUES } from './types.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const localTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM');
const id = z.string().min(1).max(64);
/** A span and a repeat cannot both be true of one task. Either would be
 *  expressible on its own; together they describe something the app has no way
 *  to draw, so the pair is refused at the door rather than stored and puzzled
 *  over later. */
const spanIsCoherent = (v, ctx) => {
    const end = typeof v.endDate === 'string' ? v.endDate : null;
    if (!end)
        return;
    if (v.startDate && end < v.startDate) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'],
            message: 'the last day cannot be before the first' });
    }
    if (v.rrule) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'],
            message: 'a task cannot both repeat and run over several days' });
    }
};
/** How long a task's notes may run. Room for an email thread pasted in as
 *  context, or a page of instructions for the agent to act on — not a document
 *  store. Shared so the web app, the API and the MCP server refuse the same
 *  thing at the same length. */
export const NOTES_MAX = 50_000;
export const createTaskInput = z.object({
    /** Optional client-generated id. A task created offline gets its id on the
     *  device, so replaying the queued create after a reconnect lands on the same
     *  row instead of inventing a second one. Absent, the server assigns one. */
    id: id.optional(),
    title: z.string().min(1).max(500),
    notes: z.string().max(NOTES_MAX).nullish(),
    /** Tag names or #slugs. Anything that does not exist yet is created. */
    tags: z.array(z.string().min(1).max(40)).max(10).optional(),
    assigneeId: id.nullish(),
    startDate: isoDate,
    /** The last day of a span. Must not be before the start, and must not be set
     *  on a repeating task. */
    endDate: isoDate.nullish(),
    startTime: localTime.nullish(),
    durationMin: z.number().int().min(1).max(1440).nullish(),
    rrule: z.string().max(500).nullish(),
    remindMinBefore: z.number().int().min(0).max(10080).nullish(),
}).superRefine(spanIsCoherent);
/** `id` is part of creating a task, never of patching one — the path carries it. */
export const updateTaskInput = createTaskInput.innerType().partial().omit({ id: true }).extend({
    /** Conflict guard, set only when replaying a queued edit: apply this patch
     *  only if nobody has touched the task since the device last saw it. Absent on
     *  a live edit, where the screen you are looking at *is* the current state. */
    ifUnmodifiedSince: z.number().int().nonnegative().optional(),
}).superRefine(spanIsCoherent);
export const rangeQuery = z.object({ from: isoDate, to: isoDate })
    .refine((v) => v.from <= v.to, { message: '`from` must not be after `to`' });
/** The state of one day of one task as the device last saw it. `none` is the
 *  ordinary case: the schedule produced the day and nothing has happened to it. */
export const occurrenceGuard = z.enum(['none', 'done', 'skipped', 'moved']);
export const completeInput = z.object({
    date: isoDate,
    done: z.boolean().default(true),
    ifStatus: occurrenceGuard.optional(),
});
export const moveInput = z.object({
    date: isoDate,
    to: isoDate,
    ifStatus: occurrenceGuard.optional(),
});
export const quickAddInput = z.object({
    text: z.string().min(1).max(500),
    /** Carried alongside the one-line text rather than parsed out of it, so an
     *  agent can create a task and attach its context in a single request —
     *  never a task that exists for a moment without the reason it was made. */
    notes: z.string().max(NOTES_MAX).nullish(),
    /** falls back to the household's today */
    today: isoDate.optional(),
});
export const hue = z.enum(HUES);
//# sourceMappingURL=schemas.js.map